import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import MuiTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import GoalButton from "../components/GoalButton";
import GoalActionButton from "../components/GoalActionButton";
import GoalPill from "../components/GoalPill";
import SuiInput from "components/SuiInput";
import ErrorAlert from "components/ErrorAlert";
import Pagination from "utils/Pagination";
import useGoalTemplates from "../useGoalTemplates";
import GoalTemplateForm from "../components/GoalTemplateForm";
import { MONTH_LABELS, percentageSum, approxEqual } from "../goals.constants";

// Fixed widths (with table-layout: fixed below) so the columns stay put
// whatever the labels are.
const TEMPLATE_TABLE_COLUMNS = [
  { label: "Template", width: "25%" },
  { label: "Period", width: "12%" },
  { label: "Client Satisfaction", width: "14%" },
  { label: "Professional", width: "13%" },
  { label: "Personal", width: "12%" },
  { label: "Total", width: "10%" },
  { label: "Actions", width: "14%", align: "center" },
];

const PAGE_LIMIT = 10;

function GoalTemplates() {
  const {
    auth: { admin },
  } = useSelector((state) => state);
  const navigate = useNavigate();

  const {
    templates,
    isLoading,
    errorMessage,
    successMessage,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    archiveTemplate,
    clearError,
    clearSuccess,
  } = useGoalTemplates(admin?.id);

  // `showForm` swaps the add/edit form in above the table; `editTarget` is the
  // template being edited (null = adding a new one).
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, [admin?.id]);

  // `isCopy`: the template only pre-fills a brand new one (Duplicate).
  const [isCopy, setIsCopy] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDelete = async () => {
    setIsDeleting(true);
    await archiveTemplate(deleteTarget.id);
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  // The full template list is small and already loaded (the setup form needs
  // all of it), so search and paging happen here rather than on the server.
  const filteredTemplates = templates.filter((template) =>
    template.label.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );
  const pageCount = Math.max(1, Math.ceil(filteredTemplates.length / PAGE_LIMIT));
  const visibleTemplates = filteredTemplates.slice(
    (Math.min(currentPage, pageCount) - 1) * PAGE_LIMIT,
    Math.min(currentPage, pageCount) * PAGE_LIMIT
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const openForm = (template = null, copy = false) => {
    setEditTarget(template);
    setIsCopy(copy);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditTarget(null);
    setIsCopy(false);
  };

  const handleSave = async (body) => {
    const ok =
      editTarget && !isCopy
        ? await updateTemplate(editTarget.id, body)
        : await createTemplate(body);
    if (ok) closeForm();
    return ok;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {errorMessage && <ErrorAlert color="error" message={errorMessage} clearError={clearError} />}
      {successMessage && (
        <ErrorAlert color="success" message={successMessage} clearError={clearSuccess} />
      )}

      <SuiBox mb={2}>
        <GoalButton
          variant="text"
          size="small"
          icon="arrow_back"
          onClick={() => navigate("/goals")}
        >
          Back to Goals
        </GoalButton>
      </SuiBox>

      <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <SuiTypography variant="h5" fontWeight="medium">
          Goal Period Templates
        </SuiTypography>
        {!showForm && (
          <GoalButton variant="primary" icon="add" onClick={() => openForm()}>
            Add Template
          </GoalButton>
        )}
      </SuiBox>

      {showForm && (
        <GoalTemplateForm
          // Remount per template so the form starts from that template's values.
          key={`${isCopy ? "copy-" : ""}${editTarget?.id || "new"}`}
          template={editTarget}
          isCopy={isCopy}
          onSave={handleSave}
          onCancel={closeForm}
        />
      )}

      {/* The list is hidden while the form is open so a template can't be
          archived (or edited twice) out from under an open edit. */}
      {!showForm && (
        <SuiBox mb={3}>
          <SuiInput
            fullWidth
            placeholder="Search template…"
            value={searchTerm}
            onChange={handleSearch}
          />
        </SuiBox>
      )}

      {!showForm && (
        <Card sx={{ mb: 3 }}>
          <SuiBox p={3}>
            {isLoading && isEmpty(templates) ? (
              <SuiBox display="flex" justifyContent="center" py={3}>
                <CircularProgress color="dark" size={26} />
              </SuiBox>
            ) : isEmpty(templates) ? (
              <SuiBox display="flex" flexDirection="column" alignItems="center" py={3}>
                <Icon fontSize="large" color="secondary" sx={{ opacity: 0.5 }}>
                  tune
                </Icon>
                <SuiTypography variant="button" color="text" fontWeight="regular" mt={1}>
                  No templates yet. Add one to start setting up goals.
                </SuiTypography>
              </SuiBox>
            ) : isEmpty(filteredTemplates) ? (
              <SuiBox display="flex" flexDirection="column" alignItems="center" py={3}>
                <SuiTypography variant="button" color="text" fontWeight="regular">
                  No templates match &quot;{searchTerm}&quot;.
                </SuiTypography>
              </SuiBox>
            ) : (
              <TableContainer sx={{ boxShadow: "none" }}>
                <MuiTable size="small" sx={{ tableLayout: "fixed", minWidth: 720 }}>
                  {/* The theme sets thead to display:block, which detaches the header from the body's column widths. */}
                  <TableHead sx={{ display: "table-header-group", p: 0 }}>
                    <TableRow>
                      {TEMPLATE_TABLE_COLUMNS.map(({ label, width, align }) => (
                        <TableCell key={label} align={align} sx={{ width }}>
                          <SuiTypography variant="caption" fontWeight="bold" color="secondary">
                            {label.toUpperCase()}
                          </SuiTypography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleTemplates.map((template) => {
                      const categorySum = percentageSum([
                        template.client_satisfaction_percentage,
                        template.professional_goals_percentage,
                        template.personal_goals_percentage,
                      ]);
                      return (
                        <TableRow key={template.id} hover>
                          <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            <SuiTypography variant="button" fontWeight="medium">
                              {template.label}
                            </SuiTypography>
                          </TableCell>
                          <TableCell>
                            <GoalPill color="info">
                              {MONTH_LABELS[template.from_month - 1]} –{" "}
                              {MONTH_LABELS[template.to_month - 1]}
                            </GoalPill>
                          </TableCell>
                          <TableCell>
                            <GoalPill>{template.client_satisfaction_percentage}%</GoalPill>
                          </TableCell>
                          <TableCell>
                            <GoalPill>{template.professional_goals_percentage}%</GoalPill>
                          </TableCell>
                          <TableCell>
                            <GoalPill>{template.personal_goals_percentage}%</GoalPill>
                          </TableCell>
                          <TableCell>
                            <GoalPill
                              color={
                                approxEqual(categorySum, template.total_percentage)
                                  ? "success"
                                  : "error"
                              }
                            >
                              {template.total_percentage}%
                              {!approxEqual(categorySum, template.total_percentage) && " ⚠"}
                            </GoalPill>
                          </TableCell>
                          <TableCell align="center">
                            <SuiBox display="flex" gap={0.75} justifyContent="center">
                              <GoalActionButton
                                icon="edit"
                                tooltip="Edit"
                                color="info"
                                onClick={() => openForm(template)}
                              />
                              <GoalActionButton
                                icon="content_copy"
                                tooltip="Duplicate"
                                color="success"
                                onClick={() => openForm(template, true)}
                              />
                              <GoalActionButton
                                icon="delete"
                                color="error"
                                tooltip={
                                  template.in_use_count > 0
                                    ? `Used by ${template.in_use_count} goal period${
                                        template.in_use_count === 1 ? "" : "s"
                                      } - can't delete`
                                    : "Delete"
                                }
                                disabled={template.in_use_count > 0}
                                onClick={() => setDeleteTarget(template)}
                              />
                            </SuiBox>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </MuiTable>
              </TableContainer>
            )}
          </SuiBox>
        </Card>
      )}

      {!showForm && !isLoading && (
        <SuiBox mb={6}>
          <Pagination
            className="pagination-bar"
            currentPage={Math.min(currentPage, pageCount)}
            totalCount={filteredTemplates.length}
            pageSize={PAGE_LIMIT}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </SuiBox>
      )}

      <Dialog open={!!deleteTarget} onClose={() => !isDeleting && setDeleteTarget(null)}>
        <DialogTitle>Delete template?</DialogTitle>
        <DialogContent>
          <SuiTypography variant="button" fontWeight="regular" color="text">
            &quot;{deleteTarget?.label}&quot; will be removed and can no longer be picked when
            setting goals.
          </SuiTypography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <GoalButton
            variant="secondary"
            onClick={() => setDeleteTarget(null)}
            disabled={isDeleting}
          >
            Cancel
          </GoalButton>
          <GoalButton
            variant="danger"
            icon="delete"
            loading={isDeleting}
            onClick={confirmDelete}
            disabled={isDeleting}
          >
            Delete
          </GoalButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default GoalTemplates;
